import { useAudioUpload } from '@/hooks/use-audio-upload';
import { useHomeUploadStore } from '@/stores/home-upload';
import { Upload } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

interface AudioDropZoneProps {
  children: React.ReactNode;
}

export const AudioDropZone = ({ children }: AudioDropZoneProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dragCounterRef = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);

  const { selectedTemplate, isUploading, isCreating, setIsUploading } =
    useHomeUploadStore(
      useShallow((s) => ({
        selectedTemplate: s.selectedTemplate,
        isUploading: s.isUploading,
        isCreating: s.isCreating,
        setIsUploading: s.setIsUploading,
      })),
    );

  const onUploadComplete = useCallback(
    (reportId: string) => {
      setIsUploading(false);
      navigate(`/reports/${reportId}`);
    },
    [navigate, setIsUploading],
  );

  const onError = useCallback(
    (message: string) => {
      setIsUploading(false);
      toast.error(message);
    },
    [setIsUploading],
  );

  const { uploadFile } = useAudioUpload({
    templateId: selectedTemplate,
    onUploadStart: () => setIsUploading(true),
    onUploadComplete,
    onError,
  });

  const disabled = isCreating || isUploading;

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);

      if (disabled) return;

      const file = e.dataTransfer.files?.[0];
      if (!file) return;

      uploadFile(file);
    },
    [disabled, uploadFile],
  );

  return (
    <div
      className="relative flex min-h-0 w-full flex-1 flex-col"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}>
      {children}
      {isDragOver && (
        <div
          className="border-primary bg-primary/5 absolute inset-4 z-50 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed backdrop-blur-sm transition-all duration-200"
          style={{ pointerEvents: 'none' }}>
          <div className="bg-primary/10 rounded-full p-4">
            <Upload className="text-primary size-10" />
          </div>
          <p className="text-foreground text-center text-lg font-medium">
            {t('home:dropAudioHere')}
          </p>
          <p className="text-muted-foreground text-center text-sm">
            MP3, WAV, M4A, FLAC... (max 100 MB)
          </p>
        </div>
      )}
    </div>
  );
};
